import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data.user);
      setTenant(response.data.tenant);
    } catch (err) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { token, user, tenant } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      setTenant(tenant);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name) => {
    try {
      setError(null);
      const response = await authAPI.register({ email, password, name });
      const { token, user, tenant } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      setTenant(tenant);
      return { success: true };
    } catch (err) {
      const data = err.response?.data;
      const message = data?.details?.[0]?.message || data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('msp_original_token');
    setUser(null);
    setTenant(null);
  };

  const updateTenant = (newTenant) => {
    setTenant(newTenant);
  };

  /**
   * Login with an existing token (used for MSP impersonation)
   * Stores the token and refreshes user/tenant state
   */
  const loginWithToken = async (token) => {
    try {
      localStorage.setItem('token', token);
      const response = await authAPI.getCurrentUser();
      setUser(response.data.user);
      setTenant(response.data.tenant);
      return { success: true };
    } catch (err) {
      localStorage.removeItem('token');
      const message = err.response?.data?.error || 'Token login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    tenant,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    loginWithToken,
    register,
    logout,
    updateTenant,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
